/**
 * The rule for who counts as "here" — kept free of imports on purpose, so it can
 * be tested without the SharePoint client (which needs tenant config at import).
 */

/** A user counts as "here" if their heartbeat is newer than this. */
export const PRESENCE_ACTIVE_MS = 2 * 60 * 1000;

export interface IPresenceUser {
  email: string;
  name: string;
  lastSeenMs: number;
}

export interface ISpRow {
  Id: number;
  Title?: string | null;      // email (unique key)
  DisplayName?: string | null;
  LastSeen?: string | null;
  /** Stamped by SharePoint itself on every write — the one clock all sessions share. */
  Modified?: string | null;
}

/**
 * Who counts as "here", judged on SharePoint's clock rather than anybody's laptop.
 *
 * Presence used to write `LastSeen = new Date()` and then keep rows newer than
 * `Date.now() - 2 min`: the WRITER's clock against the READER's. Two machines a
 * few minutes apart then disagree about who is here, and not symmetrically — the
 * laptop whose clock runs BEHIND stamps its own rows too old for everyone else's
 * cut-off, so it vanishes from every other screen while still seeing them all.
 * One person saw the other's head; the other saw only their own.
 *
 * `Modified` is set by the server, so every session's row is on one timeline. We
 * only need to know where "now" is on it, and our own row answers that: the
 * heartbeat that wrote it finished a moment ago, so its `Modified` IS the
 * server's now. The window is then measured from there, and neither laptop's
 * clock is consulted at all.
 *
 * Falls back to the local clock — the old behaviour, never worse — when we have
 * no trustworthy server "now": our own heartbeat failed this round (so our row
 * is stale and would drag the window back and resurrect ghosts), or our row has
 * not been written yet.
 */
export function selectActive(
  rows: ISpRow[],
  opts: { myEmail?: string; beatOk: boolean; clientNowMs: number; windowMs?: number },
): IPresenceUser[] {
  const windowMs = opts.windowMs ?? PRESENCE_ACTIVE_MS;
  const me = (opts.myEmail ?? "").trim().toLowerCase();
  const modifiedMs = (r: ISpRow) => (r.Modified ? new Date(r.Modified).getTime() : NaN);

  const mine = me ? rows.find((r) => (r.Title ?? "").trim().toLowerCase() === me) : undefined;
  const serverNow = opts.beatOk && mine && Number.isFinite(modifiedMs(mine)) ? modifiedMs(mine) : null;
  const refMs = serverNow ?? opts.clientNowMs;

  return rows
    .filter((r) => (r.Title ?? "").trim() !== "")
    .map((r) => {
      // Server time when SharePoint gave it to us; our own LastSeen only as a
      // last resort for a row that somehow has no Modified.
      const seen = Number.isFinite(modifiedMs(r)) ? modifiedMs(r) : r.LastSeen ? new Date(r.LastSeen).getTime() : NaN;
      return { r, seen };
    })
    .filter(({ seen }) => Number.isFinite(seen) && seen >= refMs - windowMs)
    .sort((a, b) => b.seen - a.seen)
    .map(({ r, seen }) => ({
      email: r.Title as string,
      name: (r.DisplayName ?? r.Title) as string,
      lastSeenMs: seen,
    }));
}
